// ============================================
// CRT Monitor Post-Process Effect
// ============================================
// Retro tube-display simulation: curvature + bezel, curved scanlines,
// RGB aperture grille, edge fringing, hum flicker, analog noise.

import type { IRenderContext } from '../../core/IRenderContext';
import {
  PostProcessEffect,
  type PostProcessContext,
  type PostProcessParameter,
} from '../PostProcessStack';
import { CRT_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * CRT post-processing effect
 */
export class CRTEffect extends PostProcessEffect {
  readonly type = 'crt';
  readonly displayName = 'CRT';

  // Parameters
  curvature = 0.35;
  scanlineIntensity = 0.35;
  scanlineDensity = 0.35;   // visible lines = height * density
  maskIntensity = 0.25;
  chromatic = 1.0;
  flicker = 0.3;
  noise = 0.08;
  vignette = 0.6;
  cornerRadius = 0.08;
  brightness = 1.25;

  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = CRT_LAYOUT.createWriter();

  getParameters(): PostProcessParameter[] {
    return [
      { name: 'curvature', type: 'float', min: 0, max: 1, default: 0.35, description: 'Screen curvature' },
      { name: 'scanlineIntensity', type: 'float', min: 0, max: 1, default: 0.35, description: 'Scanline darkness' },
      { name: 'scanlineDensity', type: 'float', min: 0.1, max: 1, default: 0.35, description: 'Lines per pixel row' },
      { name: 'maskIntensity', type: 'float', min: 0, max: 1, default: 0.25, description: 'RGB phosphor mask' },
      { name: 'chromatic', type: 'float', min: 0, max: 3, default: 1.0, description: 'Edge color fringing' },
      { name: 'flicker', type: 'float', min: 0, max: 1, default: 0.3, description: 'Hum flicker' },
      { name: 'noise', type: 'float', min: 0, max: 1, default: 0.08, description: 'Analog noise' },
      { name: 'vignette', type: 'float', min: 0, max: 1, default: 0.6, description: 'Tube vignette' },
      { name: 'cornerRadius', type: 'float', min: 0, max: 0.3, default: 0.08, description: 'Rounded corners' },
      { name: 'brightness', type: 'float', min: 0.5, max: 2, default: 1.25, description: 'Brightness compensation' },
    ];
  }

  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'curvature': return this.curvature;
      case 'scanlineIntensity': return this.scanlineIntensity;
      case 'scanlineDensity': return this.scanlineDensity;
      case 'maskIntensity': return this.maskIntensity;
      case 'chromatic': return this.chromatic;
      case 'flicker': return this.flicker;
      case 'noise': return this.noise;
      case 'vignette': return this.vignette;
      case 'cornerRadius': return this.cornerRadius;
      case 'brightness': return this.brightness;
      default: return 0;
    }
  }

  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'curvature': this.curvature = Math.max(0, Math.min(1, value)); break;
      case 'scanlineIntensity': this.scanlineIntensity = Math.max(0, Math.min(1, value)); break;
      case 'scanlineDensity': this.scanlineDensity = Math.max(0.05, Math.min(1, value)); break;
      case 'maskIntensity': this.maskIntensity = Math.max(0, Math.min(1, value)); break;
      case 'chromatic': this.chromatic = Math.max(0, Math.min(3, value)); break;
      case 'flicker': this.flicker = Math.max(0, Math.min(1, value)); break;
      case 'noise': this.noise = Math.max(0, Math.min(1, value)); break;
      case 'vignette': this.vignette = Math.max(0, Math.min(1, value)); break;
      case 'cornerRadius': this.cornerRadius = Math.max(0, Math.min(0.3, value)); break;
      case 'brightness': this.brightness = Math.max(0.5, Math.min(2, value)); break;
    }
  }

  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();

    const crtWgsl = getCompiledShader('postprocess/crt');
    this.shader = ctx.createShader(crtWgsl, crtWgsl);

    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'crt-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'crt-ldr',
    });
  }

  protected onRender(context: PostProcessContext): void {
    const { ctx, source, height, time, intensity, isHdrTarget } = context;

    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      curvature: this.curvature * intensity,
      scanlineIntensity: this.scanlineIntensity * intensity,
      scanlineCount: Math.max(1, Math.floor(height * this.scanlineDensity)),
      maskIntensity: this.maskIntensity * intensity,
      chromatic: this.chromatic * intensity,
      flicker: this.flicker * intensity,
      noise: this.noise * intensity,
      vignette: this.vignette * intensity,
      cornerRadius: this.cornerRadius,
      brightness: 1.0 + (this.brightness - 1.0) * intensity,
      // Wrapped so the hash/flicker inputs keep float precision on long sessions.
      time: time % 64.0,
      _pad: 0,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }

  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
